import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import axios from '../api/client'

export interface AuthUser {
  id: number
  email: string
  role: string
}

interface AuthContextValue {
  user: AuthUser | null
  token: string | null
  isAdmin: boolean
  isLoading: boolean
  login(email: string, password: string): Promise<void>
  register(email: string, password: string, inviteCode: string, openToken?: string): Promise<void>
  logout(): void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function decodeToken(token: string): AuthUser | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      return null
    }
    return { id: payload.id, email: payload.email, role: payload.role }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Capture acquisition source from the landing URL (?utm_source / ?ref) so it
    // can be attributed at registration, even if the user signs up later.
    try {
      const params = new URLSearchParams(window.location.search)
      const src = params.get('utm_source') || params.get('ref')
      if (src) localStorage.setItem('tenwords:acq_source', src.slice(0, 64))
    } catch { /* ignore */ }

    const stored = localStorage.getItem('stonks_token')
    if (stored) {
      const decoded = decodeToken(stored)
      if (decoded) {
        setToken(stored)
        setUser(decoded)
      } else {
        localStorage.removeItem('stonks_token')
      }
    }
    setIsLoading(false)

    const handleAuthLogout = () => {
      setToken(null)
      setUser(null)
      queryClient.clear()
    }
    // Re-validate token when page is restored from bfcache (e.g. after OAuth redirect)
    const handlePageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      const t = localStorage.getItem('stonks_token')
      if (!t || !decodeToken(t)) {
        setToken(null)
        setUser(null)
        queryClient.clear()
      }
    }
    window.addEventListener('auth:logout', handleAuthLogout)
    window.addEventListener('pageshow', handlePageShow)
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email: string, password: string) => {
    queryClient.clear()
    const { data } = await axios.post('/users/login', { email, password })
    if (!data.success) throw new Error(data.error || 'Login failed')
    localStorage.setItem('stonks_token', data.token)
    setToken(data.token)
    setUser(data.user)
  }

  const register = async (email: string, password: string, inviteCode: string, openToken?: string) => {
    queryClient.clear()
    const payload: Record<string, string> = { email, password }
    if (openToken) payload.open_token = openToken
    else payload.invite_code = inviteCode
    try {
      const acqSource = localStorage.getItem('tenwords:acq_source')
      if (acqSource) payload.utm_source = acqSource
    } catch { /* ignore */ }
    const { data } = await axios.post('/users/register', payload)
    if (!data.success) throw new Error(data.error || 'Registration failed')
    localStorage.setItem('stonks_token', data.token)
    setToken(data.token)
    setUser(data.user)
  }

  const logout = () => {
    localStorage.removeItem('stonks_token')
    setToken(null)
    setUser(null)
    queryClient.clear()
  }

  return (
    <AuthContext.Provider value={{
      user, token, isAdmin: user?.role === 'admin', isLoading, login, register, logout
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
